/**
 * NumberPicker — shared types, Zod schema, styling constants, and helpers.
 *
 * A stepper-style numeric input with increment/decrement buttons and a value
 * display. Supports two layout modes: inline (horizontal row) and centered
 * (vertical stack with large value display).
 *
 * Platform-specific implementations live in NumberPicker.native.tsx and NumberPicker.web.tsx.
 * Callbacks (onChange) are platform-specific and not part of the catalog schema.
 */

import { z } from 'zod';
import { actionIdSchema } from '@galapagos/product-analytics';
import { colors } from '../../elements/colors';
import { typography } from '../../elements/typography';
import { spacing } from '../../elements/spacing';
import { radius } from '../../elements/radius';
import { states } from '../../elements/states';
import {
  sizing,
  STEPPER_BUTTON_SIZE,
  STEPPER_GAP,
  REFERENCE_COLUMN_WIDTH,
  INLINE_VALUE_WIDTH,
  INLINE_STEPPER_ROW_WIDTH,
} from '../../elements/sizing';
import type { TypeStyle } from '../../elements/typography';
import type { IconName } from '../../elements/icons/registry';

// ---------------------------------------------------------------------------
// Zod schema (catalog-safe: data/variant props only, no callbacks)
// ---------------------------------------------------------------------------

export const NumberPickerLayout = z.enum(['inline', 'centered']);
export type NumberPickerLayoutType = z.infer<typeof NumberPickerLayout>;

export const colorStopSchema = z.object({
  upTo: z.number().min(0).max(1),
  background: z.string(),
  text: z.string(),
});

export type ColorStop = z.infer<typeof colorStopSchema>;

export const numberPickerPropsSchema = z.object({
  actionId: actionIdSchema.optional(),
  decrementActionId: actionIdSchema.optional(),
  incrementActionId: actionIdSchema.optional(),
  label: z.string(),
  value: z.number().int().min(0).default(0),
  /**
   * Optional read-only reference value shown as a filled coloured tile before
   * the stepper (inline layout) — e.g. the "Last 7 days" column in the
   * typical-week comparison. Green at/below min, blue above (same scale as the
   * editable value's accent). Omit for a single-column picker. Rendered on both
   * native and web (catalog parity).
   */
  referenceValue: z.number().int().optional(),
  /**
   * Caller-supplied description of what the reference tile represents (e.g.
   * "Last week"), announced to screen readers as
   * `${label}, ${referenceAccessibilityLabel} ${value}` — the row label leads
   * so sibling tiles stay distinguishable (e.g. "Monday, Last week 3"). The
   * component ships no member-facing copy of its own — authoring stays with
   * the caller (server/flow document) per the localization seam. Falls back
   * to `${label}, ${value}` when omitted.
   */
  referenceAccessibilityLabel: z.string().optional(),
  subtitle: z.string().optional(),
  // Non-negative contract: the picker is a count control with a numeric keypad
  // (no minus key), so typed entry is digit-only and the range stays ≥ 0.
  min: z.number().int().min(0).default(0),
  max: z.number().int().min(0).default(99),
  step: z.number().int().min(1).default(1),
  disabled: z.boolean().default(false),
  layout: NumberPickerLayout.default('inline'),
  colorStops: z.array(colorStopSchema).optional(),
  testID: z.string().optional(),
  decrementTestID: z.string().optional(),
  valueTestID: z.string().optional(),
  incrementTestID: z.string().optional(),
  accessibilityLabel: z.string().optional(),
  /** Caller-authored control labels; primitives never invent member-facing copy. */
  decrementAccessibilityLabel: z.string().min(1),
  incrementAccessibilityLabel: z.string().min(1),
});

export type NumberPickerProps = z.infer<typeof numberPickerPropsSchema>;

// ---------------------------------------------------------------------------
// Clamp helper
// ---------------------------------------------------------------------------

export function clampValue(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// ---------------------------------------------------------------------------
// Styling constants — all derived from design-system elements
// ---------------------------------------------------------------------------

// Stepper button + row geometry (button size, gap, reference column, inline
// value field, total row width) is defined in ../../elements/sizing so flow
// data can consume it via the `./sizing` subpath without the native barrel;
// re-exported here so the component API is unchanged.
export {
  STEPPER_BUTTON_SIZE,
  STEPPER_GAP,
  REFERENCE_COLUMN_WIDTH,
  INLINE_VALUE_WIDTH,
  INLINE_STEPPER_ROW_WIDTH,
};
export const STEPPER_BUTTON_RADIUS = radius.rounded;

// Value display
export const VALUE_DISPLAY_MIN_WIDTH = 44;
// 44px: no exact spacing-scale rung between xxl (40) and the next step, so
// this is a Figma-pinned literal (also the inline field's fixed height below).
export const VALUE_DISPLAY_HEIGHT = 44;
export const VALUE_DISPLAY_RADIUS = radius.rounded;

export const INLINE_VALUE_VERTICAL_PADDING = spacing.xs; // 4px

// Layout
export const INLINE_VERTICAL_PADDING = spacing.sm;
export const INLINE_DIVIDER_HEIGHT = 1;
export const CENTERED_GAP = spacing.lg;
export const LABEL_SUBTITLE_GAP = spacing.xs;

// Stepper +/- glyphs render as real icons (not text characters) for
// pixel-precise 24px sizing (Figma).
export const STEPPER_ICON_SIZE = sizing.icon.sm; // 24px
export const STEPPER_ICON_DECREMENT: IconName = 'minus';
export const STEPPER_ICON_INCREMENT: IconName = 'plus';

// The inline value field is editable free-typed input; number-pad keeps the
// soft keyboard numeric-only so parseInlineValue never sees alpha input on a
// stock keyboard.
export const INLINE_VALUE_KEYBOARD_TYPE = 'number-pad' as const;

// Typography
// Inline rows (e.g. the weekly day picker) carry a bold heading with a lighter
// dated sub-line beneath; the centered layout keeps its larger label/subtitle.
export const INLINE_LABEL_TYPOGRAPHY: TypeStyle = typography.b2Bold;
export const CENTERED_LABEL_TYPOGRAPHY: TypeStyle = typography.b1Bold;
export const INLINE_SUBTITLE_TYPOGRAPHY: TypeStyle = typography.b3Regular;
export const SUBTITLE_TYPOGRAPHY: TypeStyle = typography.c1Regular;
export const INLINE_VALUE_TYPOGRAPHY: TypeStyle = typography.b2Bold;
export const CENTERED_VALUE_TYPOGRAPHY: TypeStyle = typography.h1Bold;

// ---------------------------------------------------------------------------
// Colour helpers
// ---------------------------------------------------------------------------

export interface NumberPickerColors {
  label: string;
  subtitle: string;
  divider: string;
  stepperButtonBackground: string;
  stepperButtonText: string;
  stepperButtonDisabledOpacity: number;
  valueBackground: string;
  valueText: string;
}

export const DEFAULT_COLOR_STOPS: ColorStop[] = [
  { upTo: 0.5, background: colors.background.highlight, text: colors.text.primary },
  { upTo: 1.0, background: colors.background.success, text: colors.text.inverted },
];

export function numberPickerColorsFor(
  value: number,
  min: number,
  max: number,
  colorStops?: ColorStop[],
): NumberPickerColors {
  const isAboveMin = value > min;
  const range = max - min;
  const proportion = range > 0 ? (value - min) / range : 0;

  let valueBackground: string;
  let valueText: string;

  if (!isAboveMin) {
    valueBackground = colors.background.tertiary;
    valueText = colors.text.primary;
  } else {
    const stops = colorStops ?? DEFAULT_COLOR_STOPS;
    const sorted = [...stops].sort((a, b) => a.upTo - b.upTo);
    const match = sorted.find((s) => proportion <= s.upTo);
    const fallback = sorted[sorted.length - 1];
    const resolved = match ?? fallback;
    valueBackground = resolved?.background ?? colors.background.tertiary;
    valueText = resolved?.text ?? colors.text.primary;
  }

  return {
    label: colors.text.primary,
    subtitle: colors.text.secondary,
    divider: colors.stroke.divider,
    stepperButtonBackground: colors.button.secondary,
    stepperButtonText: colors.text.secondary,
    stepperButtonDisabledOpacity: 0.45,
    valueBackground,
    valueText,
  };
}

// ---------------------------------------------------------------------------
// Inline value field (Figma baseline redesign): a white, editable field with a
// neutral border (thicker/black while focused) and a coloured accent bar
// (green when at min/"free", blue when active) inset just inside the field —
// a distinct decorative element, not the field's own border-left, so the
// neutral border still traces the full box the accent sits inside. Scoped to
// the inline layout — the centered layout keeps its filled valueBackground.
// ---------------------------------------------------------------------------

export const INLINE_VALUE_BACKGROUND = colors.background.container;
export const INLINE_VALUE_BORDER_COLOR = colors.stroke.divider;
export const INLINE_VALUE_FOCUS_BORDER_COLOR = colors.stroke.primary;
export const INLINE_VALUE_BORDER_WIDTH = states.borderWidth.default;
export const INLINE_VALUE_FOCUS_BORDER_WIDTH = states.borderWidth.selected;
export const INLINE_VALUE_RADIUS = radius.m;

// The accent bar sits inset from the field's own border, not fused with it.
export const INLINE_VALUE_ACCENT_WIDTH = states.borderWidth.selected; // 2px
export const INLINE_VALUE_ACCENT_INSET_X = spacing.xs; // 4px from the left edge
export const INLINE_VALUE_ACCENT_INSET_Y = spacing.xs; // 4px from top/bottom
export const INLINE_VALUE_ACCENT_RADIUS = 0;
// Symmetric horizontal padding so the digit centers in the field itself
// (not just its content box) — the accent bar sits underneath, inset enough
// to clear a centered digit without needing extra clearance on one side.
export const INLINE_VALUE_TEXT_PADDING_HORIZONTAL = STEPPER_GAP;

/** Left-accent colour for the inline value field: green at/below min, blue above. */
export function inlineValueAccentColor(value: number, min: number): string {
  return value > min ? colors.misc.variationJ : colors.mossGreen['500'];
}

/**
 * Parse free-typed input into a clamped integer.
 *
 * The picker is a non-negative count control (the schema constrains
 * `min`/`max`/`value` to >= 0). Input that is not a clean non-negative integer
 * — anything with a sign, letters, decimals, or exponent (e.g. `-5`, `1e2`,
 * `2a`) — is rejected to `min` rather than silently reinterpreted as a
 * different count; a clean integer is clamped into range. The mobile numeric
 * keypad only produces digits, so this strict path only affects a physical
 * (web) keyboard.
 */
export function parseInlineValue(text: string, min: number, max: number): number {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return min;
  return clampValue(parseInt(trimmed, 10), min, max);
}

// ---------------------------------------------------------------------------
// Accessibility helpers
// ---------------------------------------------------------------------------

export function numberPickerAccessibleLabel(label: string, accessibilityLabel?: string): string {
  return accessibilityLabel ?? label;
}
